/* eslint-disable i18next/no-literal-string */
import Header from "~/components/header/Header";
import Footer from "~/components/home/Footer";
import {
  LandingPageHeaderH2,
  LandingPageSubheader,
} from "~/components/landing/LandingText";
import Pricing, { AddToCartButton } from "~/components/landing/Pricing";
import AllFeatures, { ShortSentences } from "~/components/landing/AllFeatures";
import DeviceFeatures from "~/components/landing/DeviceFeatures";
import PrivacyPromised from "~/components/landing/PrivacyPromised";
import EasyInstallation, {
  EasyInstallationSubText,
} from "~/components/landing/EasyInstallation";
import ObdCompatibility from "~/components/landing/ObdCompatibility";
import Reviews from "~/components/landing/Reviews";
import ReviewsAndRatings, {
  type LandingReview,
} from "~/components/landing/ReviewsAndRatings";
import BlogLinks, {
  type LandingBlogLinksData,
} from "~/components/landing/BlogLinks";
import CartAndPricing from "~/components/landing/CartAndPricing";
import MiscGridRentals from "~/components/landing/MiscGridRentals";
import MonitoringHighlightsGrid, {
  type MonitoringHighlightsCopy,
} from "~/components/landing/MonitoringHighlightsGrid";
import LoanerHero from "~/components/shoploaner/LoanerHero";
import Visibility from "~/components/shoploaner/Visibility";

import { colors } from "~/components/landing/colors";
import { imageUrl } from "~/lib/image-url";

export const handle = { sitemap: { title: "ShopLoaner" } };

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const satisfiedCustomers = imageUrl("/images/landing/three-satisfied-fleet-customers.jpg");
const reviewerImage2 = imageUrl("/images/landing/reviewer-image-2.png");
const reviewerImage4 = imageUrl("/images/shoploaner/reviewer-image-4.png");
const reviewerImage5 = imageUrl("/images/shoploaner/reviewer-image-5.png");
const fleetVansParked1 = imageUrl("/images/shoploaner/fleet-vans-parked.jpg");
const fleetVansParked2 = imageUrl("/images/shoploaner/fleet-vans-parked-2-cropped.jpg");
const dataExport = imageUrl("/images/shoploaner/data-export.png");

// Ported from bouncieweb's `pages/shoploaner.js` -> `components/LandingLoaner.js`
// (rendered there with the title/description below and `footerLite` defaulting
// to false, so the full Footer is used).
// Faithful reproduction: same section order, copy, and layout as the original.

const blogLinksData: LandingBlogLinksData = {
  heading: "Insights, Tips, and Fleet Management Strategies",
  paragraph: `Get the most out of every mile with expert tips, performance insights,
                and smart strategies to keep your vehicles and your business running smoothly.
                Explore the latest guides and updates from the Bouncie team to manage your
                fleet with confidence.`,
  blogs: [
    {
      href: "https://www.bouncie.com/blog/how-gps-tracking-technology-is-revolutionizing-fleet-management",
      image: fleetVansParked1,
      imageAlt: "A row of vans in a parking lot",
      label: "Industry Insights",
      title: "How GPS Tracking Technology is Revolutionizing Fleet Management",
    },
    {
      href: "https://www.bouncie.com/blog/the-role-of-vehicle-tracking-in-enhancing-road-safety-for-fleets",
      image: fleetVansParked2,
      imageAlt: "3 rows of vans in a parking lot",
      label: "Product Solutions",
      title: "The Role of Vehicle Tracking in Enhancing Road Safety for Fleets",
    },
    {
      href: "https://www.bouncie.com/blog/understanding-vehicle-data-exports-from-bouncie",
      image: dataExport,
      imageAlt: "A preview of a data export",
      label: "Industry Insights",
      title: "Understanding Vehicle Data Exports from Bouncie",
    },
  ],
};

const reviewsData: LandingReview[] = [
  {
    image: reviewerImage5,
    name: "ADK",
    rating: 5,
    title: "Love this product!",
    body: (
      <>
        I have a small rental car business and these work great! They show fuel
        level, battery level, engine info..they&rsquo;re really informative and
        helpful when you need to track your cars.
      </>
    ),
  },
  {
    image: reviewerImage2,
    name: "T. Rivas",
    rating: 5,
    title: "Ideal for Fleet Management!",
    body: (
      <>
        The Bouncie GPS Car Tracker is perfect for my rental car business! Its 4G
        LTE connectivity offers reliable vehicle location tracking, route
        history, and speed monitoring. The accident notification and GeoFence
        features provide extra security and peace of mind... Highly recommend!
      </>
    ),
  },
  {
    image: reviewerImage4,
    name: "E. Wiseman",
    rating: 5,
    title: "Reliable & Customizable Tracking.",
    body: (
      <>
        I have six of these trackers for my fleet now, and they literally allow
        me to sleep at night. The accuracy is impressive, with no spotty
        coverage, and the triggers work great. They also keep adding features.
      </>
    ),
  },
];

// The seven strings that make the shared monitoring grid ShopLoaner's.
const highlightsCopy: MonitoringHighlightsCopy = {
  locationSubtitle: "Real-time fleet visibility.",
  locationBody: (
    <>
      Whether it&rsquo;s a few vehicles or a growing fleet, Bouncie captures
      every second of each trip, past and present, all on one live map. From
      customer handoff to vehicle return, you&rsquo;ll always know where each
      loaner is. Manage with confidence.
    </>
  ),
  habitsOverlayTitle: "Driving behavior made visible.",
  habitsBody: (
    <>
      Get instant insights into how your loaner vehicles are being driven. Track
      speed, rapid acceleration, hard braking, and more. Safer trips start with
      visibility. Their habits. Your protection.
    </>
  ),
  accidentBody: (
    <>
      Bouncie uses advanced sensors to detect accidents as they happen and sends
      you a real-time SMS alert. No guesswork. No delay. Respond quickly.
      Prioritize safety every time.
    </>
  ),
  weatherBody: (
    <>
      We&rsquo;re looking out for your fleet. When severe weather is reported
      near a vehicle, Bouncie sends a real-time alert with the details. React
      confidently, no matter the forecast.
    </>
  ),
  geoZonesBody: (
    <>
      Get notified the moment a loaner enters or exits a zone you define. Whether
      it&rsquo;s your service lot, a customer&rsquo;s location, or a restricted
      area, Bouncie keeps you updated in real time.
    </>
  ),
};

export default function ShopLoaner() {
  return (
    <>
      {/* React 19 hoists these into the document <head> (no wrapper element —
          a nested <head> collides with root.tsx's document head). The original
          built the canonical from `window.location.origin`; pinned to a literal
          here so the server and client markup agree. */}
      <title>GPS Tracking for ShopLoaner Dealerships | Bouncie</title>
      <meta
        name="description"
        content="Manage and protect your loaner vehicles with real-time GPS tracking, driving behavior alerts, and automated insights from Bouncie."
      />
      <link rel="canonical" href="https://www.bouncie.com/shoploaner" />

      <Header />

      {/* LandingPageGlobalStyles: color fadedBlack2 + balanced text wrap */}
      <div className="[text-wrap:balance] text-[#0B1019]">
        <LoanerHero />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <LandingPageHeaderH2 className="mb-4">
            Smarter Loaner Management Starts Here
          </LandingPageHeaderH2>
          <LandingPageSubheader>
            From vehicle handoff to return, Bouncie keeps you informed every
            mile.
          </LandingPageSubheader>
          <MiscGridRentals />
        </div>

        <ReviewsAndRatings
          heading="Trusted by Automotive Professionals"
          reviewsData={reviewsData}
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <Pricing className="mb-16">
            {/* PricingDisclaimer */}
            <div
              className="mt-[18px] rounded-[10px] p-[15px] text-[14px] [text-wrap:inherit]"
              style={{ background: colors.blueLight8 }}
            >
              <strong>ShopLoaner</strong> charges an additional monthly
              connection fee. See vendor for details.
            </div>
          </Pricing>
          <Visibility
            title="Visibility That Drives Confidence"
            description={
              <>
                Bouncie gives dealerships real-time visibility, trip insights,
                and the confidence to hand off keys knowing every vehicle is
                monitored.
              </>
            }
          />
          <LandingPageHeaderH2 className="mb-4">
            Know more. Worry less.
          </LandingPageHeaderH2>
          <LandingPageSubheader>
            The connected fleet solution used by thousands of dealerships
            nationwide.
          </LandingPageSubheader>
          <MonitoringHighlightsGrid {...highlightsCopy} />
        </div>

        <AllFeatures
          bottomContent={
            <>
              <div className="mt-4 mb-8">
                <ShortSentences>No contracts.</ShortSentences>
                <ShortSentences>No hidden fees.</ShortSentences>
                <ShortSentences>No activation fees.</ShortSentences>
              </div>
              {/* CustomAddToCartButton: 18px text-bold, 1rem/1.5rem padding,
                  width unset at every breakpoint. */}
              <AddToCartButton data-sid="shoploaner-add-to-cart" className="w-auto px-6 py-4 font-bold md:w-auto" />
            </>
          }
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <DeviceFeatures />
          <PrivacyPromised className="mb-20" />
          <EasyInstallation
            comboBottom
            preHeading={<>Yes, it&rsquo;ll work in your vehicle!</>}
            heading={
              <>
                <EasyInstallationSubText>No tools.</EasyInstallationSubText>
                <EasyInstallationSubText>No wires.</EasyInstallationSubText>
                <EasyInstallationSubText>
                  It just plugs in.
                </EasyInstallationSubText>
              </>
            }
          />
          <div id="obd-compatibility-scroll-target" />
          <ObdCompatibility comboTop />
          <Reviews
            carAndDriverImageAlt="Two women on a journey in a car"
            forbesImage={satisfiedCustomers}
            forbesImageAlt="Three smiling customers"
          />
        </div>

        <BlogLinks blogLinksData={blogLinksData} />
        <CartAndPricing />

        <Footer />
      </div>
    </>
  );
}
